import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Select } from '../../../shared/components/Select';
import { useOutreachTemplates, useUpdateOutreachTemplate } from '../hooks/useOutreach';
import {
  CHANNEL_LABELS,
  ENGAGEMENT_MODEL_LABELS,
  type OutreachChannel,
  type OutreachEngagementModel,
  type OutreachTemplate,
} from '../types';
import { TemplateFormModal } from './TemplateFormModal';

const ALL = '';

const withAll = (labels: Record<string, string>, allLabel: string) => [
  { value: ALL, title: allLabel },
  ...Object.entries(labels).map(([value, title]) => ({ value, title })),
];

const ACTIVE_OPTIONS = [
  { value: ALL, title: 'Active and inactive' },
  { value: 'true', title: 'Active only' },
  { value: 'false', title: 'Inactive only' },
];

/**
 * The reusable message-template library, one list across every Engagement Model — the
 * replacement for the static tcs-outreach-kit HTML. Filters are server-side (the controller
 * takes engagementModel / channel / isActive).
 *
 * There is no DELETE route: retiring a template is the isActive toggle, so the send log can
 * keep pointing at the template a message came from.
 */
export function TemplatesPanel({ canManage }: { canManage: boolean }) {
  const [engagementModel, setEngagementModel] = useState(ALL);
  const [channel, setChannel] = useState(ALL);
  const [active, setActive] = useState(ALL);
  const [editing, setEditing] = useState<OutreachTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: templates = [], isLoading, error } = useOutreachTemplates({
    engagementModel: (engagementModel || undefined) as OutreachEngagementModel | undefined,
    channel: (channel || undefined) as OutreachChannel | undefined,
    isActive: active === ALL ? undefined : active === 'true',
  });
  const update = useUpdateOutreachTemplate();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Engagement model"
          size="small"
          className="w-56"
          options={withAll(ENGAGEMENT_MODEL_LABELS, 'All models')}
          value={engagementModel}
          onChange={setEngagementModel}
        />
        <Select
          label="Channel"
          size="small"
          className="w-44"
          options={withAll(CHANNEL_LABELS, 'All channels')}
          value={channel}
          onChange={setChannel}
        />
        <Select
          label="Status"
          size="small"
          className="w-48"
          options={ACTIVE_OPTIONS}
          value={active}
          onChange={setActive}
        />
        {canManage && (
          <Button size="sm" className="h-[40px] ml-auto" onClick={() => setCreating(true)}>
            New template
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-500">Could not load templates.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && templates.length === 0 && (
        <p className="text-sm text-muted-foreground">No templates match these filters.</p>
      )}

      {templates.length > 0 && (
        <ul className="space-y-3">
          {templates.map((template) => (
            <li key={template.id} className="bg-card rounded-lg border border-border p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{template.name}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {ENGAGEMENT_MODEL_LABELS[template.engagementModel]}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {CHANNEL_LABELS[template.channel]}
                    </span>
                    {template.originAiDrafted && (
                      <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        From AI draft
                      </span>
                    )}
                    {!template.isActive && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-[40px]"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          id: template.id,
                          payload: { isActive: !template.isActive },
                        })
                      }
                    >
                      {template.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-[40px]"
                      onClick={() => setEditing(template)}
                    >
                      Edit
                    </Button>
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-foreground whitespace-pre-wrap">{template.body}</p>
            </li>
          ))}
        </ul>
      )}

      {creating && <TemplateFormModal onClose={() => setCreating(false)} />}
      {editing && <TemplateFormModal template={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
